<script setup lang="ts">
import { computed } from 'vue';
import { useDialogPluginComponent, useQuasar } from 'quasar';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import StandardInput from '@/components/StandardInput.vue';
import SubmissionMetaGrid from '@/components/dialogs/SubmissionMetaGrid.vue';
import { Constants } from '@/constants';
import type { Locales, QuestionAnswerDto } from '@/services/types/api.types';
import { useQuestionAnswersStore } from '@/stores/questionAnswers.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getFullName } from '@/helpers/text.helper';

const props = defineProps<{
  questionAnswer: QuestionAnswerDto;
  languageEnabled: Locales[];
}>();

const { t } = useI18n();
const $q = useQuasar();
const qaStore = useQuestionAnswersStore();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const { handleSubmit, values } = useForm<QuestionAnswerDto>({
  initialValues: {
    ...props.questionAnswer,
    answer: props.questionAnswer.answer ?? '',
  },
  validationSchema: yup.object({
    question: yup.string().trim().required(),
    answer: yup.string().trim().required(),
    is_public: yup.boolean().required(),
  }),
});
const { value: answer, errorMessage: answerError, setValue: setAnswer } = useField<string>('answer');
const { value: isPublic } = useField<boolean>('is_public');

const dialogTitle = computed(() => t('project-module.questionAnswer.dialogs.title'));

const getStatusLabel = (status: number) => {
  switch (status) {
    case Constants.ProjectModule.QuestionAnswer.Status.Pending:
      return t('project-module.questionAnswer.status.pending');
    case Constants.ProjectModule.QuestionAnswer.Status.Answered:
      return t('project-module.questionAnswer.status.answered');
    case Constants.ProjectModule.QuestionAnswer.Status.Junk:
      return t('project-module.questionAnswer.status.junk');
    default:
      return '';
  }
};

const isJunk = () => props.questionAnswer.status === Constants.ProjectModule.QuestionAnswer.Status.Junk;
const isAnswered = () => props.questionAnswer.status === Constants.ProjectModule.QuestionAnswer.Status.Answered;

const participantName = () => getFullName(props.questionAnswer.user);

const onSendAnswer = handleSubmit(async (formValues) => {
  await wrapSavingOperation(
    async () =>
      await qaStore.saveQuestionAnswer({
        ...props.questionAnswer,
        question: formValues.question,
        answer: formValues.answer,
        is_public: formValues.is_public,
      }),
    () => onDialogOK(),
  );
});

const onClose = () => {
  $q.dialog({
    title: t('project-module.questionAnswer.dialogs.junkTitle'),
    message: t('project-module.questionAnswer.dialogs.junkMessage'),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await wrapSavingOperation(
      async () => await qaStore.junkQuestionAnswer(props.questionAnswer),
      () => onDialogOK(),
    );
  });
};

const onReopen = async () => {
  await wrapSavingOperation(
    async () => await qaStore.reopenQuestionAnswer(props.questionAnswer),
    () => onDialogOK(),
  );
};
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent class="question-answer-dialog">
    <q-card class="q-dialog-plugin question-answer-dialog__card" style="width: min(800px, 96vw); max-width: 96vw">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
      </q-card-section>
      <q-separator />
      <q-card-section class="question-answer-dialog__content">
        <submission-meta-grid
          class="q-mb-lg"
          :items="[
            { label: t('global.label.name'), value: participantName() },
            { label: t('global.label.status'), value: getStatusLabel(props.questionAnswer.status) },
          ]"
        />
        <standard-input
          name="question"
          :label="t('global.label.question')"
          type="textarea"
          :readonly="isJunk()"
          outlined
          autogrow
        />
        <template v-if="!isJunk()">
          <q-field
            :label="t('global.label.answer')"
            stack-label
            borderless
            class="q-mt-sm"
            :error="!!answerError"
            :error-message="answerError"
          >
            <template v-slot:control>
              <q-editor
                :model-value="answer"
                @update:model-value="setAnswer($event)"
                class="full-width q-mt-sm"
                :toolbar="[
                  ['left', 'center', 'right', 'justify'],
                  ['bold', 'italic', 'strike', 'underline'],
                  ['undo', 'redo'],
                  ['hr', 'link'],
                  ['unordered', 'outdent', 'indent'],
                  ['removeFormat'],
                ]"
              />
            </template>
          </q-field>
          <div class="q-mt-sm">
            <q-toggle v-model="isPublic" :label="t('project-module.questionAnswer.publicToggle')" />
            <div v-if="isAnswered()" class="text-caption text-grey q-mt-xs">
              {{ t('project-module.questionAnswer.dialogs.updateNote') }}
            </div>
          </div>
        </template>
        <template v-else>
          <div class="question-answer-dialog__meta-item q-mt-md">
            <div class="text-caption text-grey-7">{{ t('global.label.answer') }}</div>
            <div class="question-answer-dialog__readonly-answer q-mt-xs" v-html="values.answer || ''" />
          </div>
        </template>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn
          v-if="!isJunk()"
          color="negative"
          flat
          :label="t('project-module.questionAnswer.dialogs.buttons.junkQuestion')"
          @click="onClose"
        />
        <q-space />
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <template v-if="!isJunk()">
          <q-btn
            color="primary"
            :label="
              isAnswered()
                ? t('project-module.questionAnswer.dialogs.buttons.updateAnswer')
                : t('project-module.questionAnswer.dialogs.buttons.sendAnswer')
            "
            @click="onSendAnswer"
          />
        </template>
        <q-btn
          v-else
          color="primary"
          :label="t('project-module.questionAnswer.dialogs.buttons.reopenQuestion')"
          @click="onReopen"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.question-answer-dialog {
  &__card {
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
  }

  &__content {
    overflow-y: auto;
  }

  &__meta-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__readonly-answer {
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fafafa;
  }
}
</style>
